import React, { useEffect, useState } from "react";
import axios from "axios";
import { DataGrid, GridToolbar, GridActionsCellItem } from "@mui/x-data-grid";
import {
	Box,
	Typography,
	TextField,
	CircularProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
} from "@mui/material";
import { FiTrash2, FiEdit2, FiInbox } from "react-icons/fi";
import { toast } from "react-toastify";
import BRANDING from "../config/branding";
import { API_URL } from '../config/api';

const UsersPage = () => {
	const [users, setUsers] = useState([]);
	const [loading, setLoading] = useState(true);
	const [search, setSearch] = useState("");
	const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
	const [selectedUser, setSelectedUser] = useState(null);
	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [editForm, setEditForm] = useState({
		id: "",
		full_name: "",
		role: "",
		email: "",
	});

	const token = localStorage.getItem("token");

	const fetchUsers = async () => {
		try {
			const res = await axios.get(`${API_URL}/allusers.php`, {
				headers: { Authorization: `Bearer ${token}` },
			});
			if (res.data.status) {
				setUsers(res.data.users);
			} else {
				toast.error(res.data.message || "Failed to load users.");
			}
		} catch (err) {
			console.error(err);
			toast.error("Failed to fetch users.");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchUsers();
	}, []);

	const handleEditClick = (user) => {
		setEditForm({
			id: user.id,
			full_name: user.full_name,
			role: user.role,
			email: user.email,
		});
		setEditDialogOpen(true);
	};

	const roles = ["admin", "customer_support"];

	const handleDeleteClick = (user) => {
		setSelectedUser(user);
		setConfirmDialogOpen(true);
	};

	const handleEditSubmit = async () => {
		try {
			const res = await axios.post(
				"http://localhost:8080/edit_user.php",
				editForm,
				{
					headers: { Authorization: `Bearer ${token}` },
				}
			);
			if (res.data.status) {
				toast.success("User updated successfully");
				setEditDialogOpen(false);
				fetchUsers();
			} else {
				toast.error(res.data.message || "Failed to update user.");
			}
		} catch (err) {
			console.error(err);
			toast.error("Error updating user.");
		}
	};

	const confirmDelete = async () => {
		try {
			await axios.delete("http://localhost:8080/delete_user.php", {
				headers: { Authorization: `Bearer ${token}` },
				data: { user_id: selectedUser.id },
			});
			toast.success("User deleted successfully");
			setConfirmDialogOpen(false);
			setSelectedUser(null);
			fetchUsers(); // Refresh list
		} catch (err) {
			console.error(err);
			toast.error("Failed to delete user.");
		}
	};

	const filteredUsers = users.filter((user) =>
		user.full_name.toLowerCase().includes(search.toLowerCase())
	);

	const columns = [
		{ field: "id", headerName: "ID", width: 90 },
		{ field: "full_name", headerName: "Full Name", flex: 1 },
		{ field: "email", headerName: "Email", flex: 1.2  },
		{
    field: "role",
    headerName: "Role",
    width: 180,
    renderCell: (params) => {
        const role = params.value;

        return (
            <span
                style={{
                    padding: "4px 12px",
                    borderRadius: "999px",
                    fontSize: "12px",
                    fontWeight: "600",
                    backgroundColor:
                        role === "admin"
                            ? "#EEF4FF"
                            : "#ECFDF5",
                    color:
                        role === "admin"
                            ? "#1E2F97"
                            : "#16A34A",
                }}
            >
                {role.replace("_", " ").toUpperCase()}
            </span>
        );
    },
},
		{ field: "created_at", headerName: "Created At", width: 180 },
		{
			field: "actions",
			type: "actions",
			headerName: "Actions",
			width: 120,
			getActions: (params) => [
				<GridActionsCellItem
					icon={<FiEdit2 style={{ color: "#1E2F97" }} />}
					label='Edit'
					onClick={() => handleEditClick(params.row)}
				/>,
				<GridActionsCellItem
					icon={<FiTrash2 className='text-red-700' />}
					label='Delete'
					onClick={() => handleDeleteClick(params.row)}
				/>,
			],
		},
	];

	return (
		<Box
				sx={{
					height: 600,
					width: "100%",
					padding: 3,
					bgcolor: "white",
					borderRadius: 3,
					border: "1px solid #E2E8F0",
					boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
				}}
			>
			<Typography
			
				  variant='h4'
				sx={{
					fontWeight: 700,
					color: BRANDING.primaryColor,
				}}
					>
				User Management
			</Typography>
			 <Typography
        variant='body2'
        sx={{
            color: "#64748B",
            mt: 0.5,
        }}
    >
        Manage portal users and access permissions
    </Typography>

			<TextField
				label='Search by Name'
				variant='outlined'
				size='small'
				fullWidth
				margin='normal'
				value={search}
				onChange={(e) => setSearch(e.target.value)}
				sx={{
					"& .MuiOutlinedInput-root": {
					"& fieldset": { borderColor: "#FECACA" },
"&:hover fieldset": { borderColor: BRANDING.primaryColor },
"&.Mui-focused fieldset": { borderColor: BRANDING.primaryColor },
					},
				}}
			/>

			{loading ? (
				<Box className='flex items-center justify-center mt-16'>
					<CircularProgress sx={{ color: BRANDING.primaryColor }} />
				</Box>
			) : filteredUsers.length === 0 ? (
				<Box className='text-center text-gray-500 mt-20'>
					<FiInbox className='text-6xl mx-auto mb-2 text-red-500' />
					<Typography variant='subtitle1'>No users available</Typography>
				</Box>
			) : (
				<DataGrid
					rows={filteredUsers}
					columns={columns}
					getRowId={(row) => row.id}
					pageSizeOptions={[10, 20, 50]}
					initialState={{
						pagination: {
							paginationModel: { pageSize: 10 },
						},
						sorting: {
							sortModel: [{ field: "created_at", sort: "desc" }],
						},
					}}
					slots={{ toolbar: GridToolbar }}
					slotProps={{ toolbar: { showQuickFilter: true } }}
					sx={{
							cursor: "pointer",
							border: "1px solid #E2E8F0",
							borderRadius: 2,

							"& .MuiDataGrid-columnHeaders": {
								backgroundColor: "#FEF2F2",
								color: BRANDING.primaryColor,
								fontWeight: 700,
								borderBottom: "2px solid #E2E8F0",
							},

							"& .MuiDataGrid-row:hover": {
								backgroundColor: "#FEF2F2",
							},

							"& .MuiDataGrid-cell": {
								borderBottom: "1px solid #F1F5F9",
							},

							"& .MuiDataGrid-footerContainer": {
								backgroundColor: "#F8FAFC",
								borderTop: "1px solid #E2E8F0",
							},

							"& .MuiTablePagination-root": {
								color: BRANDING.primaryColor,
							},

							"& .MuiIconButton-root": {
								color: BRANDING.primaryColor,
							},

							"& .MuiDataGrid-cell:focus": {
								outline: "none",
							},
						}}
				/>
			)}

			{/* Edit Dialog */}
			<Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
				<DialogTitle sx={{  backgroundColor: "#FEF2F2",
        color: BRANDING.primaryColor,
        fontWeight: 700, }}>
					Edit User
				</DialogTitle>
				<DialogContent>
					<TextField
						label='Full Name'
						value={editForm.full_name}
						onChange={(e) =>
							setEditForm((prev) => ({ ...prev, full_name: e.target.value }))
						}
						fullWidth
						margin='normal'
					/>

					<TextField
						label='Email'
						value={editForm.email}
						fullWidth
						margin='normal'
						disabled
					/>

					<TextField
						label='Role'
						select
						value={editForm.role}
						onChange={(e) =>
							setEditForm((prev) => ({ ...prev, role: e.target.value }))
						}
						fullWidth
						margin='normal'
						SelectProps={{
							native: true,
						}}
					>
						<option value='' disabled>
							Select Role
						</option>
						{roles.map((role) => (
							<option key={role} value={role}>
								{role.charAt(0).toUpperCase() + role.slice(1)}
							</option>
						))}
					</TextField>
				</DialogContent>

				<DialogActions>
					<Button onClick={() => setEditDialogOpen(false)} color='inherit'>
						Cancel
					</Button>
					<Button
						onClick={handleEditSubmit}
						variant='contained'
						sx={{ 	bgcolor: BRANDING.primaryColor, "&:hover": { bgcolor: "#B91C1C", } }}
					>
						Save
					</Button>
				</DialogActions>
			</Dialog>

			{/* Delete Dialog */}
			<Dialog
				open={confirmDialogOpen}
				onClose={() => setConfirmDialogOpen(false)}
			>
				<DialogTitle sx={{  backgroundColor: "#FEF2F2",
        color: BRANDING.primaryColor,
        fontWeight: 700, }}>
					Confirm Delete
				</DialogTitle>
				<DialogContent>
					Are you sure you want to delete{" "}
					<strong>{selectedUser?.full_name}</strong>?
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setConfirmDialogOpen(false)} color='inherit'>
						Cancel
					</Button>
					<Button
						onClick={confirmDelete}
						variant='contained'
						sx={{ bgcolor: "#dc2626", "&:hover": { bgcolor: "#991b1b" } }}
					>
						Delete
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default UsersPage;
